import { useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgBootstrap } from '@/hooks/useOrganization';
import { Icon } from '@/components/primitives/Icon';
import { Button } from '@/components/primitives/Button';
import { IconButton } from '@/components/primitives/IconButton';
import { Avatar } from '@/components/primitives/Avatar';
import { auth } from '@/core/firebase';

export default function Header() {
  const { user } = useAuth();
  const { orgs, activeOrgId } = useOrgBootstrap();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const initials = user?.displayName
    ? user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : (user?.email?.slice(0, 2).toUpperCase() ?? '??');
  const resolvedOrgId = activeOrgId;
  const activeOrg = orgs.find((org) => String(org.id) === String(resolvedOrgId)) ?? orgs[0];
  const orgName =
    activeOrg?.name ?? (resolvedOrgId ? `Organization #${resolvedOrgId}` : 'No organization');

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && !menuRef.current?.contains(target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  async function handleLogout() {
    try {
      await signOut(auth);
      setMenuOpen(false);
    } catch (err) {
      console.error('Failed to sign out', err);
    }
  }

  return (
    <header className="fixed top-0 right-0 w-full md:w-[calc(100%-16rem)] h-16 bg-white/80 backdrop-blur-md flex justify-between items-center px-4 md:px-8 z-40 border-b border-stone-200/60">
      {/* Search */}
      <div className="flex items-center w-1/2">
        <div className="relative w-full max-w-md group">
          <Icon
            name="search"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-outline-variant group-focus-within:text-primary transition-colors"
          />
          <input
            className="w-full bg-surface-container-low border-none rounded-xl pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-primary/20 placeholder:text-stone-400 outline-none"
            placeholder="Search templates, formats, or assets..."
            type="text"
          />
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-3 md:gap-6">
        <div className="hidden md:flex items-center gap-2">
          <IconButton icon="notifications" variant="ghost" />
          <IconButton icon="settings" variant="ghost" />
        </div>

        {/* Upgrade pill */}
        <Button variant="primary" size="sm" className="hidden sm:flex font-black text-[10px]">
          <Icon name="arrow_upward" className="text-[14px]" />
          Upgrade
        </Button>

        {/* Credits ring */}
        <div className="hidden sm:flex items-center gap-3 bg-stone-50 px-3 py-1.5 rounded-full border border-stone-100">
          <div className="relative flex items-center justify-center w-8 h-8">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle
                className="stroke-stone-200"
                cx="18"
                cy="18"
                fill="none"
                r="16"
                strokeWidth="3"
              />
              <circle
                className="transition-all duration-1000 ease-out"
                cx="18"
                cy="18"
                fill="none"
                r="16"
                stroke="#8e84ad"
                strokeDasharray="100"
                strokeDashoffset="86.6"
                strokeLinecap="round"
                strokeWidth="3"
              />
            </svg>
            <span className="absolute text-[9px] font-black text-stone-600">13%</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-stone-800 leading-none">10 / 75</span>
            <span className="text-[8px] uppercase tracking-tighter font-black text-primary">
              Credits Left
            </span>
          </div>
        </div>

        <div className="hidden sm:block h-8 w-px bg-stone-200" />

        {/* User avatar */}
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="flex items-center gap-3 cursor-pointer"
            aria-expanded={menuOpen}
          >
            <div className="hidden md:block text-right">
              <p className="text-xs font-black text-on-surface">{initials}</p>
              <p className="text-[10px] text-primary uppercase font-black tracking-tighter">PRO</p>
            </div>
            <Avatar src={user?.photoURL} initials={initials} size="md" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-3 w-48 rounded-xl border border-outline-variant/20 bg-surface-container-lowest shadow-xl py-2">
              <div className="px-4 pb-2 border-b border-outline-variant/10">
                <p className="text-[9px] uppercase font-black tracking-widest text-on-surface-variant">
                  Organization
                </p>
                <p className="text-sm font-bold text-on-surface truncate">{orgName}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm font-bold text-on-surface hover:bg-surface-container-low transition-colors cursor-pointer"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
